import React, { useState } from "react";
import { Check, Gift, Loader2, Minus, Plus } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { inr } from "@/lib/api";

export const HamperBuilder = ({ product }) => {
  const [selected, setSelected] = useState({});
  const [boxes, setBoxes] = useState(1);
  const [busy, setBusy] = useState(false);
  const { addHamper } = useCart();
  const options = product.hamper_items || [];
  const required = product.hamper_required_count;
  const basePrice = Number(product.base_price ?? product.price ?? 0);
  const count = Object.values(selected).reduce((sum, value) => sum + value, 0);
  const additions = options.reduce((sum, option, index) => (
    sum + Number(option.additional_price ?? option.price ?? 0) * (selected[index] || 0)
  ), 0);
  const total = basePrice + additions;
  const choices = options.flatMap((option, index) => {
    if (!selected[index]) return [];
    const choice = { quantity: selected[index] };
    if (option.custom_item_id) {
      choice.custom_item_id = option.custom_item_id;
    } else {
      choice.product_id = option.product_id;
      choice.variant_label = option.variant_label;
    }
    return [choice];
  });
  const selectedItemCapacity = choices.length
    ? Math.min(...options.flatMap((option, index) => (
      selected[index] ? [Math.floor(option.available_stock / selected[index])] : []
    )))
    : 0;
  const maxBoxes = Math.min(selectedItemCapacity, Number(product.stock ?? 0));
  const valid = count === required && Number.isInteger(boxes) && boxes >= 1 && boxes <= maxBoxes;
  const change = (index, delta) => setSelected((current) => ({
    ...current,
    [index]: Math.max(0, (current[index] || 0) + delta),
  }));
  const submit = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      await addHamper(product, choices, boxes);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      className="mt-6 rounded-xl border border-[#D9C3A5] bg-white p-4 sm:p-6"
      data-testid="hamper-builder"
    >
      <div className="flex items-center gap-2 text-[#2D5A47]">
        <Gift size={20} />
        <h2 className="text-base font-semibold md:text-lg">Make it their own</h2>
      </div>
      <p className="mt-2 text-sm text-[#64748B]">
        Choose exactly {required} items per box. Mix favourites or repeat an item.
      </p>
      <div
        className="mt-4 flex items-center justify-between text-sm font-semibold"
        aria-live="polite"
        data-testid="hamper-slot-indicator"
      >
        <span>{count} of {required} items selected</span>
        {count === required && <Check size={18} className="text-[#2D5A47]" />}
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[#F4F1EA]">
        <div
          className="h-full bg-[#2D5A47] transition-[width]"
          style={{ width: `${Math.min(100, count / required * 100)}%` }}
        />
      </div>
      <div className="mt-5 divide-y divide-[#E2DCD2]" data-testid="hamper-options">
        {options.map((option, index) => {
          const additionalPrice = Number(option.additional_price ?? option.price ?? 0);
          return (
            <div
              key={option.custom_item_id || `${option.product_id}-${option.variant_label}`}
              className="flex items-center gap-3 py-4"
              data-testid={`hamper-option-${index}`}
            >
              {option.image && (
                <img src={option.image} alt="" className="h-14 w-14 rounded-lg object-cover" />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{option.name}</p>
                {option.variant_label && (
                  <p className="text-xs text-[#64748B]">{option.variant_label}</p>
                )}
                <p
                  className="mt-1 text-sm font-semibold text-[#2D5A47]"
                  data-testid={`hamper-option-price-${index}`}
                >
                  {additionalPrice > 0 ? `+${inr(additionalPrice)}` : "Included"}
                </p>
                <p
                  className="text-xs text-[#64748B]"
                  data-testid={`hamper-option-stock-${index}`}
                >
                  {option.available_stock > 0
                    ? `${option.available_stock} available`
                    : "Currently unavailable"}
                </p>
              </div>
              <div className="flex shrink-0 items-center rounded-lg border border-[#E2DCD2]">
                <button
                  type="button"
                  disabled={!selected[index] || busy}
                  onClick={() => change(index, -1)}
                  aria-label={`Remove ${option.name}`}
                  className="p-2 transition-colors hover:bg-[#F4F1EA] disabled:opacity-30"
                  data-testid={`hamper-item-remove-${index}`}
                >
                  <Minus size={14} />
                </button>
                <span
                  className="min-w-6 text-center text-sm"
                  data-testid={`hamper-item-quantity-${index}`}
                >
                  {selected[index] || 0}
                </span>
                <button
                  type="button"
                  disabled={
                    busy
                    || count >= required
                    || (selected[index] || 0) >= option.available_stock
                  }
                  onClick={() => change(index, 1)}
                  aria-label={`Add ${option.name}`}
                  className="p-2 transition-colors hover:bg-[#F4F1EA] disabled:opacity-30"
                  data-testid={`hamper-item-add-${index}`}
                >
                  <Plus size={14} />
                </button>
              </div>
            </div>
          );
        })}
        {!options.length && (
          <p className="py-4 text-sm" data-testid="hamper-options-empty">
            No selectable items are currently available.
          </p>
        )}
      </div>
      <div className="mt-4 border-t border-[#E2DCD2] pt-5">
        <div className="flex items-center justify-between gap-4">
          <label className="text-sm font-medium" htmlFor="hamper-box-count">
            Number of boxes
          </label>
          <input
            id="hamper-box-count"
            type="number"
            min="1"
            max={maxBoxes || undefined}
            step="1"
            value={boxes}
            onChange={(event) => setBoxes(
              event.target.value === "" ? "" : Number(event.target.value),
            )}
            className="w-24 rounded-lg border border-[#E2DCD2] px-3 py-2"
            data-testid="hamper-box-quantity"
          />
        </div>
        {count === required && boxes > maxBoxes && (
          <p className="mt-2 text-sm text-red-700" role="alert" data-testid="hamper-stock-error">
            Only {maxBoxes} boxes available with these selections.
          </p>
        )}
        <div className="mt-4 flex justify-between text-sm">
          <span>Base box price</span>
          <span data-testid="hamper-base-price">{inr(basePrice)}</span>
        </div>
        <div className="mt-2 flex justify-between text-sm">
          <span>Selected item additions</span>
          <span data-testid="hamper-additions-total">{inr(additions)}</span>
        </div>
        <div className="mt-2 flex justify-between text-lg font-semibold text-[#2D5A47]">
          <span>Per box</span>
          <span data-testid="hamper-unit-total">{inr(total)}</span>
        </div>
        <div
          className="mt-2 flex justify-between text-lg font-semibold text-[#2D5A47]"
          aria-live="polite"
        >
          <span>Your total</span>
          <span data-testid="hamper-total">{inr(total * (Number(boxes) || 0))}</span>
        </div>
        <button
          type="button"
          disabled={!valid || busy}
          onClick={submit}
          className={[
            "mt-5 flex w-full items-center justify-center gap-2 rounded-lg",
            "bg-[#2D5A47] py-3.5 font-semibold text-white transition-colors",
            "hover:bg-[#214435] disabled:cursor-not-allowed disabled:bg-[#E8EBE6]",
            "disabled:text-[#64748B]",
          ].join(" ")}
          data-testid="hamper-add-to-cart"
        >
          {busy ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Checking availability…
            </>
          ) : count !== required ? (
            `Select ${required - count} more item${required - count === 1 ? "" : "s"}`
          ) : (
            "Add to Cart"
          )}
        </button>
      </div>
    </section>
  );
};
